import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Alert, Button, Form, InputNumber, Switch, Typography } from 'antd'
import { FlaskConical, Play } from 'lucide-react'
import { useTRPC } from '@/trpc/react.ts'
import type { BotDetailData } from './bot-detail.tsx'

export function BotTraining({ bot, onSaved }: { bot: BotDetailData; onSaved: () => Promise<void> }) {
  const trpc = useTRPC()
  const [credential, setCredential] = useState<{ id: string; token: string }>()
  const update = useMutation(trpc.bot.training.mutationOptions({ onSuccess: onSaved }))
  const start = useMutation(trpc.bot.startTraining.mutationOptions({ onSuccess: setCredential }))
  const revoke = useMutation(trpc.bot.revokeTraining.mutationOptions({ onSuccess: () => setCredential(undefined) }))
  return (
    <div className="space-y-5">
      {(update.error || start.error || revoke.error) && (
        <Alert type="error" title={(update.error ?? start.error ?? revoke.error)?.message} />
      )}
      <section className="bg-surface border-border flex flex-wrap items-center justify-between gap-4 rounded-xl border p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <FlaskConical className="text-accent mt-0.5 size-5 shrink-0" />
          <div>
            <h2 className="text-content m-0 text-base font-semibold">管理员演练授权</h2>
            <p className="text-content-muted mt-1.5 mb-0 text-sm leading-6">
              开启后可创建训练授权，与客户群日常运行相互隔离。
            </p>
          </div>
        </div>
        <Switch
          aria-label="允许管理员演练"
          checkedChildren="已开启"
          unCheckedChildren="已关闭"
          checked={bot.trainingEnabled}
          loading={update.isPending}
          onChange={(trainingEnabled) => update.mutate({ id: bot.id, expectedVersion: bot.version, trainingEnabled })}
        />
      </section>
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <section className="bg-surface border-border min-w-0 rounded-xl border">
          <div className="border-border border-b p-5 sm:px-6">
            <h2 className="text-content m-0 text-base font-semibold">创建演练</h2>
            <p className="text-content-muted mt-1.5 mb-0 text-sm">选择测试客户，配置本次演练的操作范围。</p>
          </div>
          <Form<{ customerId: number; bindingId?: number; live?: boolean }>
            layout="vertical"
            className="p-5! sm:p-6!"
            disabled={!bot.trainingEnabled || !bot.isEnabled}
            onFinish={(value) =>
              start.mutate({
                botId: bot.id,
                customerId: value.customerId,
                bindingId: value.bindingId,
                live: value.live ?? false
              })
            }
          >
            {!bot.isEnabled && <Alert className="mb-5!" type="info" showIcon title="请先启用机器人，再创建演练。" />}
            <div className="grid gap-x-5 sm:grid-cols-2">
              <Form.Item
                name="customerId"
                label="测试客户 ID"
                rules={[{ required: true, message: '请输入测试客户 ID' }]}
              >
                <InputNumber className="w-full!" min={1} precision={0} placeholder="输入客户 ID" />
              </Form.Item>
              <Form.Item name="bindingId" label="接收群绑定 ID" extra="真实出单和发送时必填">
                <InputNumber className="w-full!" min={1} precision={0} placeholder="输入已绑定的群 ID" />
              </Form.Item>
            </div>
            <Form.Item
              name="live"
              label="允许本次真实出单和发送"
              valuePropName="checked"
              extra="默认演练。开启后必须指定已绑定接收群，生成的正式单据会实际发送。"
            >
              <Switch />
            </Form.Item>
            <Button htmlType="submit" type="primary" icon={<Play className="size-4" />} loading={start.isPending}>
              创建训练授权
            </Button>
          </Form>
        </section>
        <aside className="border-border flex flex-col gap-5 rounded-xl border p-5 sm:p-6" aria-label="演练说明">
          <h2 className="text-content m-0 text-sm font-semibold">演练须知</h2>
          <dl className="m-0 space-y-5 text-sm">
            <div>
              <dt className="text-content font-medium">30 分钟有效</dt>
              <dd className="text-content-muted mt-1 ml-0 leading-6">训练授权仅用于本次演练，结束后可主动撤销。</dd>
            </div>
            <div>
              <dt className="text-content font-medium">按权限交集执行</dt>
              <dd className="text-content-muted mt-1 ml-0 leading-6">
                使用个人 MCP 密钥，权限取当前员工与机器人的交集。
              </dd>
            </div>
            <div>
              <dt className="text-content font-medium">知识审核后发布</dt>
              <dd className="text-content-muted mt-1 ml-0 leading-6">长期知识先在管理员工作目录编辑，审核后再发布。</dd>
            </div>
          </dl>
        </aside>
      </div>
      {credential && (
        <Alert
          type="success"
          title="训练 Token（仅显示一次）"
          description={
            <div>
              <Typography.Paragraph copyable className="break-all">
                {credential.token}
              </Typography.Paragraph>
              <p>
                在管理员训练进程中设置 SUPER_MEGA_TRAINING_TOKEN，并使用个人 MCP Key。SUPER_MEGA_USER_MCP_URL
                指向个人入口 /api/mcp。不要写入客户网关配置。
              </p>
              <Button onClick={() => revoke.mutate({ id: credential.id })} loading={revoke.isPending}>
                结束本次授权
              </Button>
            </div>
          }
        />
      )}
    </div>
  )
}
